// src/lib/db.js
import {
  collection, doc, addDoc, setDoc, updateDoc, deleteDoc,
  getDoc, getDocs, query, where, orderBy, limit,
  serverTimestamp, increment, onSnapshot, startAfter,
  Timestamp, writeBatch
} from 'firebase/firestore';
import { RRule } from 'rrule';
import { db } from './firebase';

// ── TOWNS & COUNTIES ─────────────────────────────────────

// County IDs are hand-picked slugs (e.g. 'clare'), not auto-generated —
// towns store this exact string in their own countyId field, and the
// county picker routes using it directly, so it has to be predictable
// and typo-proof rather than a random Firestore ID.
export const createCounty = async (id, data) => {
  await setDoc(doc(db, 'counties', id), {
    ...data,
    createdAt: serverTimestamp(),
  });
};

export const updateCounty = async (id, data) => {
  await updateDoc(doc(db, 'counties', id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const deleteCounty = async (id) => {
  await deleteDoc(doc(db, 'counties', id));
};

export const getCounties = async () => {
  const snap = await getDocs(query(collection(db, 'counties'), orderBy('name')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getTowns = async (countyId = null) => {
  let q = countyId
    ? query(collection(db, 'towns'), where('countyId', '==', countyId), orderBy('name'))
    : query(collection(db, 'towns'), orderBy('name'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getTown = async (townId) => {
  const snap = await getDoc(doc(db, 'towns', townId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// ── TOWN LAUNCH CHECKLIST ────────────────────────────────
// The repeatable go-live process — same steps used for Loughrea,
// reusable for every Wave 1A town without reinventing the sequence.
export const LAUNCH_CHECKLIST_TEMPLATE = [
  { id: 'curate_events',    label: 'Curate 100 verified events',        sub: 'Accurate dates, images, venues' },
  { id: 'populate_services', label: 'Populate services directory',       sub: 'Real listings, not empty state' },
  { id: 'skill_share_examples', label: 'Add Skill Share example listings', sub: 'Aim for 4–5 to start' },
  { id: 'moderation_check', label: 'Content moderation spot-check',     sub: 'Confirm flagging works as expected' },
  { id: 'scanner_accuracy', label: 'Test scanner accuracy',             sub: 'Run against curated data first' },
  { id: 'first_scan',       label: 'Run first full scanner scan',       sub: '' },
  { id: 'partner_meeting',  label: 'Local partner meeting scheduled',   sub: 'Credit union / community group' },
  { id: 'signage',          label: 'Physical signage ordered',          sub: 'Window stickers, QR codes' },
  { id: 'go_live',          label: 'Go live confirmed',                 sub: '' },
];

export const toggleChecklistItem = async (townId, itemId, done) => {
  const townRef = doc(db, 'towns', townId);
  const snap = await getDoc(townRef);
  const current = snap.exists() ? (snap.data().launchChecklist || {}) : {};
  await updateDoc(townRef, {
    launchChecklist: {
      ...current,
      [itemId]: done
        ? { done: true, completedAt: serverTimestamp() }
        : { done: false, completedAt: null },
    },
  });
};

// ── EVENTS ───────────────────────────────────────────────

// Compute next occurrence from RRULE string
export const getNextOccurrence = (event) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (!event.rrule || event.repeat === 'once') {
    return event.date?.toDate ? event.date.toDate() : new Date(event.date);
  }

  try {
    const rule = RRule.fromString(event.rrule);
    const next = rule.after(now, true);
    return next || (event.date?.toDate ? event.date.toDate() : new Date(event.date));
  } catch {
    return event.date?.toDate ? event.date.toDate() : new Date(event.date);
  }
};

// Build an RRULE string from a start date + a simple repeat label.
// Returns null for 'once' (no recurrence).
export const buildRRule = (startDate, repeat) => {
  if (!repeat || repeat === 'once') return null;
  const freqMap = {
    weekly:      { freq: RRule.WEEKLY, interval: 1 },
    fortnightly: { freq: RRule.WEEKLY, interval: 2 },
    monthly:     { freq: RRule.MONTHLY, interval: 1 },
  };
  const cfg = freqMap[repeat];
  if (!cfg) return null;
  const rule = new RRule({ freq: cfg.freq, interval: cfg.interval, dtstart: startDate });
  return rule.toString();
};

export const listenToEvents = (townId, callback, category = null) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  let q = query(
    collection(db, 'events'),
    where('townId', '==', townId),
    where('status', '==', 'published'),
    where('nextDate', '>=', now),
    orderBy('nextDate')
  );

  if (category) {
    q = query(q, where('category', '==', category));
  }

  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, err => {
    console.error('listenToEvents failed:', err);
  });
};

export const getEvent = async (eventId) => {
  const snap = await getDoc(doc(db, 'events', eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getOrganiserEvents = async (organiserId) => {
  const q = query(
    collection(db, 'events'),
    where('organiserId', '==', organiserId),
    orderBy('nextDate')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Public-facing version for the Organiser Profile page — only what a
// visitor should actually see: published and still upcoming. The plain
// getOrganiserEvents above is for the organiser's own dashboard, where
// they need to see pending/hidden/past events too.
export const getOrganiserPublicEvents = async (organiserId) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const q = query(
    collection(db, 'events'),
    where('organiserId', '==', organiserId),
    where('status', '==', 'published'),
    where('nextDate', '>=', now),
    orderBy('nextDate')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getMyFollowForOrganiser = async (organiserId, uid) => {
  const q = query(
    collection(db, 'follows'),
    where('organiserId', '==', organiserId),
    where('userId', '==', uid)
  );
  const snap = await getDocs(q);
  return !snap.empty;
};

export const createEvent = async (eventData) => {
  return await addDoc(collection(db, 'events'), {
    ...eventData,
    status: 'published',
    interestCount: 0,
    viewCount: 0,
    ticketCount: 0,
    promoted: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const updateEvent = async (eventId, updates) => {
  await updateDoc(doc(db, 'events', eventId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteEvent = async (eventId) => {
  await deleteDoc(doc(db, 'events', eventId));
};

export const incrementView = async (eventId) => {
  await updateDoc(doc(db, 'events', eventId), {
    viewCount: increment(1),
  });
};

// ── Skill Share ───────────────────────────────────────

export const createSkillListing = async (data) => {
  return await addDoc(collection(db, 'skills'), {
    ...data,
    status: 'live',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const updateSkillListing = async (skillId, updates) => {
  await updateDoc(doc(db, 'skills', skillId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteSkillListing = async (skillId) => {
  await deleteDoc(doc(db, 'skills', skillId));
};

export const getSkillListings = async (townId = null) => {
  let q = townId
    ? query(collection(db, 'skills'), where('townId', '==', townId), where('status', '==', 'live'), orderBy('createdAt', 'desc'))
    : query(collection(db, 'skills'), where('status', '==', 'live'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAllSkillListings = async () => {
  const q = query(collection(db, 'skills'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getMySkillListings = async (uid) => {
  const q = query(collection(db, 'skills'), where('postedBy', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── COMMUNITY: NOTICES ──────────────────────────────────────

export const createNotice = async (data) => {
  return await addDoc(collection(db, 'notices'), {
    ...data,
    status: 'live',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const deleteNotice = async (noticeId) => {
  await deleteDoc(doc(db, 'notices', noticeId));
};

export const updateNotice = async (noticeId, updates) => {
  await updateDoc(doc(db, 'notices', noticeId), { ...updates, updatedAt: serverTimestamp() });
};

export const getNotices = async (townId) => {
  const q = query(
    collection(db, 'notices'),
    where('townId', '==', townId),
    where('status', '==', 'live'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAllNotices = async () => {
  const q = query(collection(db, 'notices'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getMyNotices = async (uid) => {
  const q = query(collection(db, 'notices'), where('postedBy', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── COMMUNITY: MEET LOCALS ──────────────────────────────────
// Activity-first framing — "join me for a walk", not "I'm lonely".
// 30-day auto-expiry handled server-side (expireMeetLocals scheduled fn).

export const createMeetLocal = async (data) => {
  return await addDoc(collection(db, 'meetLocals'), {
    ...data,
    status: 'live',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const deleteMeetLocal = async (meetLocalId) => {
  await deleteDoc(doc(db, 'meetLocals', meetLocalId));
};

export const updateMeetLocal = async (meetLocalId, updates) => {
  await updateDoc(doc(db, 'meetLocals', meetLocalId), { ...updates, updatedAt: serverTimestamp() });
};

export const getMeetLocals = async (townId) => {
  const q = query(
    collection(db, 'meetLocals'),
    where('townId', '==', townId),
    where('status', '==', 'live'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAllMeetLocals = async () => {
  const q = query(collection(db, 'meetLocals'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getMyMeetLocals = async (uid) => {
  const q = query(collection(db, 'meetLocals'), where('postedBy', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── SERVICES: RECOMMENDATIONS ───────────────────────────────
// One doc per user under services/{serviceId}/recommendations/{uid} —
// the rules themselves block a second recommendation from the same
// person (create fails if the doc already exists at that path... it
// doesn't auto-block on Firestore side, so we check existence first).

export const hasRecommended = async (serviceId, uid) => {
  const snap = await getDoc(doc(db, 'services', serviceId, 'recommendations', uid));
  return snap.exists();
};

export const recommendService = async (serviceId, uid) => {
  const already = await hasRecommended(serviceId, uid);
  if (already) return false;
  await setDoc(doc(db, 'services', serviceId, 'recommendations', uid), {
    uid, createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'services', serviceId), {
    recommendCount: increment(1),
  });
  return true;
};

export const unrecommendService = async (serviceId, uid) => {
  await deleteDoc(doc(db, 'services', serviceId, 'recommendations', uid));
  await updateDoc(doc(db, 'services', serviceId), {
    recommendCount: increment(-1),
  });
};

// ── SERVICES: DEALS ──────────────────────────────────────────
// Time-limited offers. expiresAt is a plain JS Date at creation time —
// the expireDeals scheduled function marks status 'expired' once past it.

export const createDeal = async (data) => {
  return await addDoc(collection(db, 'deals'), {
    ...data,
    status: 'live',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const deleteDeal = async (dealId) => {
  await deleteDoc(doc(db, 'deals', dealId));
};

export const updateDeal = async (dealId, updates) => {
  await updateDoc(doc(db, 'deals', dealId), { ...updates, updatedAt: serverTimestamp() });
};

export const getDeals = async (townId) => {
  const now = new Date();
  const q = query(
    collection(db, 'deals'),
    where('townId', '==', townId),
    where('status', '==', 'live'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  // Belt-and-braces client-side filter too, in case the hourly expiry
  // job hasn't caught a just-expired deal yet
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(deal => {
      const exp = deal.expiresAt?.toDate ? deal.expiresAt.toDate() : new Date(deal.expiresAt);
      return exp > now;
    });
};

export const getAllDeals = async () => {
  const q = query(collection(db, 'deals'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getMyDeals = async (uid) => {
  const q = query(collection(db, 'deals'), where('postedBy', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── INTERESTS ────────────────────────────────────────────

export const markInterested = async (eventId, userId) => {
  await addDoc(collection(db, 'interests'), {
    eventId, userId,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'events', eventId), {
    interestCount: increment(1),
  });
};

export const removeInterest = async (eventId, userId) => {
  const q = query(
    collection(db, 'interests'),
    where('eventId', '==', eventId),
    where('userId', '==', userId)
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
  await updateDoc(doc(db, 'events', eventId), {
    interestCount: increment(-1),
  });
};

export const getUserInterests = async (userId) => {
  const q = query(collection(db, 'interests'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().eventId);
};

// ── REGISTRATIONS ────────────────────────────────────────

export const registerForEvent = async (eventId, userId, name, email, eventDate) => {
  await addDoc(collection(db, 'registrations'), {
    eventId, userId, name, email,
    eventDate: eventDate || null, // stored for auto-deletion after 90 days
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'events', eventId), {
    registrationCount: increment(1),
  });
};

export const getEventRegistrations = async (eventId) => {
  const q = query(collection(db, 'registrations'), where('eventId', '==', eventId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getUserRegistrations = async (userId) => {
  const q = query(collection(db, 'registrations'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── USERS ────────────────────────────────────────────────

export const getUser = async (userId) => {
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createUserProfile = async (userId, data) => {
  await addDoc(collection(db, 'users'), {
    uid: userId,
    ...data,
    // Role NOT set here — only via Cloud Function custom claims
    createdAt: serverTimestamp(),
  });
};

export const updateUserProfile = async (userId, data) => {
  await updateDoc(doc(db, 'users', userId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const setUserTown = async (userId, townId, countyId) => {
  await updateDoc(doc(db, 'users', userId), {
    defaultTownId: townId,
    defaultCountyId: countyId,
    updatedAt: serverTimestamp(),
  });
};

export const saveFCMToken = async (userId, token) => {
  await updateDoc(doc(db, 'users', userId), {
    fcmToken: token,
    fcmUpdatedAt: serverTimestamp(),
  });
};

// ── SPONSORS ─────────────────────────────────────────────

export const getTownSponsors = async (townId) => {
  const q = query(
    collection(db, 'sponsors'),
    where('townId', '==', townId),
    where('active', '==', true),
    orderBy('tierOrder')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── SPONSOR ANALYTICS ────────────────────────────────────
// No impression/click tracking existed before this — these write the
// counters that getSponsorAnalytics reads back for the admin dashboard.

export const incrementSponsorImpression = async (sponsorId) => {
  try {
    await updateDoc(doc(db, 'sponsors', sponsorId), { impressions: increment(1) });
  } catch { /* non-critical — never block the page render on this */ }
};

export const incrementSponsorClick = async (sponsorId) => {
  try {
    await updateDoc(doc(db, 'sponsors', sponsorId), { clicks: increment(1) });
  } catch { /* non-critical */ }
};

export const getSponsorAnalytics = async () => {
  const sponsorsSnap = await getDocs(collection(db, 'sponsors'));
  const sponsors = sponsorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Town-level context so a sponsor's numbers sit alongside real activity
  const townIds = [...new Set(sponsors.map(s => s.townId).filter(Boolean))];
  const townStatsById = {};
  await Promise.all(townIds.map(async (townId) => {
    const eventsSnap = await getDocs(
      query(collection(db, 'events'), where('townId', '==', townId), where('status', '==', 'published'))
    );
    let totalViews = 0, totalInterests = 0;
    eventsSnap.docs.forEach(d => {
      totalViews += d.data().viewCount || 0;
      totalInterests += d.data().interestCount || 0;
    });
    townStatsById[townId] = {
      liveEvents: eventsSnap.size,
      interests: totalInterests,
      eventViews: totalViews,
    };
  }));

  return sponsors.map(s => ({
    ...s,
    impressions: s.impressions || 0,
    clicks: s.clicks || 0,
    ctr: s.impressions ? Math.round(((s.clicks || 0) / s.impressions) * 1000) / 10 : 0,
    townStats: townStatsById[s.townId] || { liveEvents: 0, interests: 0, eventViews: 0 },
  }));
};

// ── SERVICES ─────────────────────────────────────────────

export const getTownServices = async (townId, category = null) => {
  let q = query(
    collection(db, 'services'),
    where('townId', '==', townId),
    where('active', '==', true),
    orderBy('tierOrder'),
    orderBy('name')
  );
  const snap = await getDocs(q);
  let services = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (category) services = services.filter(s => s.category === category);
  return services;
};

// Public submission — any signed-in user. Goes live immediately, same as
// events; the moderateService trigger pulls it back to pending_review only
// if flagged. Always free tier — paid tier upgrades stay admin-only.
export const createService = async (serviceData, userId) => {
  return await addDoc(collection(db, 'services'), {
    ...serviceData,
    submittedBy: userId,
    claimedBy: userId,        // self-submitted = auto-claimed by the poster
    claimStatus: 'claimed',
    tier: 'free',
    active: true,
    status: 'published',
    createdAt: serverTimestamp(),
  });
};

// Claimed owner (incl. a self-submitted listing, which is auto-claimed by
// its poster) can delete their own listing — mirrors the ownership check
// already used for editing. Admin/town admin can delete any listing
// regardless of claimedBy, per firestore.rules.
export const deleteService = async (serviceId) => {
  await deleteDoc(doc(db, 'services', serviceId));
};

export const approveService = async (serviceId) => {
  await updateDoc(doc(db, 'services', serviceId), {
    status: 'published',
    approvedAt: serverTimestamp(),
    pendingReason: null,
  });
};

export const rejectService = async (serviceId) => {
  await updateDoc(doc(db, 'services', serviceId), {
    status: 'hidden',
    active: false,
    rejectedAt: serverTimestamp(),
  });
};

export const getService = async (serviceId) => {
  const snap = await getDoc(doc(db, 'services', serviceId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Owner (claimedBy) edits their own listing directly — no re-moderation
// needed since it's their own business and they're already claimed/verified.
export const updateOwnedService = async (serviceId, updates) => {
  await updateDoc(doc(db, 'services', serviceId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const getMyServices = async (uid) => {
  const q = query(collection(db, 'services'), where('claimedBy', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ── SERVICE CLAIMS ────────────────────────────────────────────
// A business claiming a listing Grace bulk-imported. Goes to a review
// queue — never auto-approved, so nobody can claim a business they
// don't actually own just by asking.

export const requestServiceClaim = async ({ serviceId, serviceName, requestedBy, contactEmail, contactPhone, website, message }) => {
  return await addDoc(collection(db, 'serviceClaims'), {
    serviceId, serviceName, requestedBy, contactEmail, contactPhone: contactPhone || null, website: website || null, message: message || null,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
};

export const getMyClaimForService = async (serviceId, uid) => {
  const q = query(
    collection(db, 'serviceClaims'),
    where('serviceId', '==', serviceId),
    where('requestedBy', '==', uid)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const getAllServiceClaims = async () => {
  const q = query(collection(db, 'serviceClaims'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const approveServiceClaim = async (claimId, serviceId, requestedBy, claimContactInfo = {}) => {
  const batch = writeBatch(db);
  batch.update(doc(db, 'serviceClaims', claimId), { status: 'approved', resolvedAt: serverTimestamp() });

  const serviceUpdate = { claimedBy: requestedBy, claimStatus: 'claimed' };
  // Apply whatever verified contact details the claimant provided —
  // approving a claim should mean the listing reflects the real business's
  // details, not just a permission flag with no actual data change.
  if (claimContactInfo.contactEmail) serviceUpdate.email = claimContactInfo.contactEmail;
  if (claimContactInfo.contactPhone) serviceUpdate.phone = claimContactInfo.contactPhone;
  if (claimContactInfo.website) serviceUpdate.website = claimContactInfo.website;

  batch.update(doc(db, 'services', serviceId), serviceUpdate);
  await batch.commit();
};

export const rejectServiceClaim = async (claimId) => {
  await updateDoc(doc(db, 'serviceClaims', claimId), { status: 'rejected', resolvedAt: serverTimestamp() });
};


// ── FOLLOWS ──────────────────────────────────────────────

export const getUserFollows = async (userId) => {
  const q = query(collection(db, 'follows'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().organiserId);
};

export const getOrganiserFollowerCount = async (organiserId) => {
  const snap = await getDoc(doc(db, 'organiser_stats', organiserId));
  return snap.exists() ? (snap.data()?.followerCount || 0) : 0;
};

// ── COMMUNITY IMPACT DASHBOARD ───────────────────────────

export const getCommunityStats = async () => {
  const [eventsSnap, usersSnap, townsSnap, volunteersSnap, interestsSnap] = await Promise.all([
    getDocs(query(collection(db, 'events'), where('status', '==', 'published'))),
    getDocs(collection(db, 'users')),
    getDocs(query(collection(db, 'towns'), where('status', '==', 'live'))),
    getDocs(query(collection(db, 'events'), where('volunteersNeeded', '==', true))),
    getDocs(collection(db, 'interests')),
  ]);

  return {
    totalEvents:       eventsSnap.size,
    totalUsers:        usersSnap.size,
    liveTowns:         townsSnap.size,
    volunteerOpps:     volunteersSnap.size,
    totalInteractions: interestsSnap.size,
  };
};

export const getTownStats = async (townId) => {
  const [eventsSnap, usersSnap] = await Promise.all([
    getDocs(query(collection(db, 'events'), where('townId', '==', townId))),
    getDocs(query(collection(db, 'users'), where('defaultTownId', '==', townId))),
  ]);
  return {
    events: eventsSnap.size,
    users:  usersSnap.size,
  };
};

// Impact Report — one shareable summary combining everything a sponsor,
// credit union partner, or grant application would want to see for a town.
// Built to be pulled on demand, not waiting on a calendar month to pass.
export const getImpactReport = async (townId) => {
  const [
    publishedEventsSnap, allEventsSnap, usersSnap,
    servicesSnap, skillsSnap, sponsorAnalytics,
  ] = await Promise.all([
    getDocs(query(collection(db, 'events'), where('townId', '==', townId), where('status', '==', 'published'))),
    getDocs(query(collection(db, 'events'), where('townId', '==', townId))),
    getDocs(query(collection(db, 'users'), where('defaultTownId', '==', townId))),
    getDocs(query(collection(db, 'services'), where('townId', '==', townId), where('active', '==', true))),
    getDocs(query(collection(db, 'skills'), where('townId', '==', townId))),
    getSponsorAnalytics(),
  ]);

  let totalInterests = 0, totalViews = 0;
  publishedEventsSnap.docs.forEach(d => {
    totalInterests += d.data().interestCount || 0;
    totalViews += d.data().viewCount || 0;
  });

  const organiserCount = usersSnap.docs.filter(d => d.data().role === 'organiser').length;
  const publishedSkills = skillsSnap.docs.filter(d => d.data().status !== 'pending_review').length;
  const townSponsors = sponsorAnalytics.filter(s => s.townId === townId);

  return {
    townId,
    liveEvents: publishedEventsSnap.size,
    totalEventsEverPosted: allEventsSnap.size,
    totalRSVPs: totalInterests,
    totalEventViews: totalViews,
    totalUsers: usersSnap.size,
    organiserCount,
    activeServices: servicesSnap.size,
    skillShareListings: publishedSkills,
    sponsors: townSponsors,
    generatedAt: new Date(),
  };
};

// ── ADMIN ────────────────────────────────────────────────

export const getAllEvents = async (townId = null) => {
  let q = townId
    ? query(collection(db, 'events'), where('townId', '==', townId), orderBy('createdAt', 'desc'))
    : query(collection(db, 'events'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getPendingReviewEvents = async () => {
  const q = query(
    collection(db, 'events'),
    where('status', '==', 'pending_review'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const approveEvent = async (eventId) => {
  await updateDoc(doc(db, 'events', eventId), {
    status: 'published',
    approvedAt: serverTimestamp(),
    pendingReason: null,
  });
};

export const rejectEvent = async (eventId) => {
  await updateDoc(doc(db, 'events', eventId), {
    status: 'hidden',
    rejectedAt: serverTimestamp(),
  });
};

export const getAllUsers = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAuditLog = async (limitCount = 50) => {
  const q = query(
    collection(db, 'audit_log'),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getScannerLogs = async () => {
  const q = query(
    collection(db, 'scanner_logs'),
    orderBy('runAt', 'desc'),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Alias for backward compatibility
export const incrementEventView = incrementView;
export const setUserRole = async (userId, role) => {
  const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
  await updateDoc(doc(db, 'users', userId), { role, updatedAt: serverTimestamp() });
};

// Genuinely anonymous — no user ID attached, matching the whole point of the
// Accessibility page's suggestion box. Write-only; only admin can read these back.
export const submitAccessibilityFeedback = async (message) => {
  await addDoc(collection(db, 'accessibility_feedback'), {
    message: message.trim(),
    createdAt: serverTimestamp(),
  });
};
